import { StateContext } from '@ngxs/store';
import { TableStateModel } from '../table.state.model';
import { Pagination } from '@core/models/pagination.model';
import { inject, Injectable } from '@angular/core';
import { AccountManagementService } from '@core/services/account-management.service';
import { TableActions } from '../table.actions';
import { catchError, tap, throwError } from 'rxjs';
import { NotificationFacade } from '@core/facades/notification.facade';
import { getDefaultTableState } from '../table.state';

@Injectable({
  providedIn: 'root',
})
export class AccountsHandler {
  constructor() {}
  accountService = inject(AccountManagementService);
  notify = inject(NotificationFacade);

  handleAccountsLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.accountService.viewAccountList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'accounts',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'accounts',
            error: error.message,
          })
        );
      },
    });
  }

  handleGetMetadata(ctx: StateContext<TableStateModel>) {
    return this.accountService.getAccountListMetaData().subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.GetAccountsMetadataSuccess({
            accountMetadata: response.data,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.GetAccountsMetadataFailure({
            error: error.message,
          })
        );
      },
    });
  }

  getAccountsMetadataSuccess(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsMetadataSuccess
  ) {
    const currentState = ctx.getState();

    const currentAccount = currentState.accounts || getDefaultTableState();
    ctx.patchState({
      accounts: {
        ...currentAccount,
        accountMetadata: action.payload.accountMetadata,
      },
    });
  }

  getAccountsMetadataFailure(
    ctx: StateContext<TableStateModel>,
    action: TableActions.GetAccountsMetadataFailure
  ) {
    const currentState = ctx.getState();
    const currentAccount = currentState.accounts || getDefaultTableState();

    ctx.patchState({
      accounts: {
        ...currentAccount,
        error: action.payload.error,
      },
    });
  }

  filterAccountList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterAccountList
  ) {
    return this.accountService
      .filterAccountList(action.payload.filter)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: action.payload.table,
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: action.payload.table,
              error: error.message,
            })
          );
        },
      });
  }

  blockAccount(
    ctx: StateContext<TableStateModel>,
    action: TableActions.BlockAccount
  ) {
    this.accountService.blockAccount(action.payload.accountId).subscribe({
      next: () => {
        const state = ctx.getState();
        const accounts = state.accounts?.data.map((account) =>
          account.id === action.payload.accountId
            ? { ...account, is_blocked: true }
            : account
        );
        this.notify.success('Account restricted');

        ctx.patchState({
          accounts: {
            ...state.accounts,
            data: accounts,
          },
        } as Partial<TableStateModel>);
      },
      error: (error) => {
        this.notify.error('Account restriction failed: ', error);
      },
    });
  }

  unblockAccount(
    ctx: StateContext<TableStateModel>,
    action: TableActions.UnblockAccount
  ) {
    this.accountService.unblockAccount(action.payload.accountId).subscribe({
      next: () => {
        const state = ctx.getState();
        const accounts = state.accounts?.data.map((account) =>
          account.id === action.payload.accountId
            ? { ...account, is_blocked: false }
            : account
        );
        this.notify.success('Account unrestricted');

        ctx.patchState({
          accounts: {
            ...state.accounts,
            data: accounts,
          },
        } as Partial<TableStateModel>);
      },
      error: (error) => {
        this.notify.error('Account restriction failed: ', error);
      },
    });
  }

  deleteAccount(
    ctx: StateContext<TableStateModel>,
    action: TableActions.DeleteAccount
  ) {
    this.accountService.deleteAccount(action.payload.accountId).subscribe({
      next: () => {
        const state = ctx.getState();
        const accounts = state.accounts?.data.filter(
          (account) => account.id !== action.payload.accountId
        );
        this.notify.success('Account deleted successfully');

        ctx.patchState({
          accounts: {
            ...state.accounts,
            data: accounts,
          },
        } as Partial<TableStateModel>);
      },
      error: (error) => {
        this.notify.error('Account deletion failed: ', error);
      },
    });
  }
}
